//LIBRARIES
import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useT } from "@transifex/react";
import { useGeneralContext } from "../../../context/AppGeneralContext";
import { useTestCreationContext } from "../../../context/TestCreationContext";
import { updateTestInServerDebounced } from "../../../helpers/testAutosaveDebounce";
import InputWithActions from "../../inputWithActions/InputWithActions";

import { Label, LabelWrapper, Wrapper } from "./StyledComponents";

export interface ExerciseSheetTitleProps {
    setIsTestBeingSynced?: React.Dispatch<React.SetStateAction<boolean>>;
}

const ExerciseSheetTitle: React.FC<ExerciseSheetTitleProps> = (props) => {
    const { userInfo, userId } = useGeneralContext();
    const { testName, setTestName, testContent, setIsTitleSynced, setIsTestSynced, saveTest } =
        useTestCreationContext();

    const t = useT();
    const t_exerciseTitle = t("Exercise sheet title:", { _tags: "CreateTest,ExerciseSheetTitle" });

    const [currentTitle, setCurrentTitle] = useState(testName);
    const [isEditing, setIsEditing] = useState(false);
    const [isNameChanged, setIsNameChanged] = useState(false);

    const saveTestData = useCallback(() => {
        // Drop any pending exercise autosave — it carries a stale (autogenerated)
        // title and would otherwise overwrite the edited title with the old name.
        // Cancelling also drops that autosave's isTestSynced=true resolution, so this
        // full save owns both synced flags (title + test) instead.
        updateTestInServerDebounced.cancel();
        props.setIsTestBeingSynced?.(true);
        saveTest(testContent)
            .then(() => {
                setIsTitleSynced(true);
                setIsTestSynced(true);
            })
            .catch(() => {
                setIsTitleSynced(false);
                setIsTestSynced(false);
            })
            .finally(() => {
                props.setIsTestBeingSynced?.(false);
            });
    }, [props, saveTest, setIsTitleSynced, setIsTestSynced, testContent]);

    useEffect(() => {
        if (!(testContent?.length > 0)) return;

        if (isNameChanged) {
            if (userId && !userInfo.isAnonymousUser) saveTestData();
            setIsNameChanged(false);
        } else {
            setIsTitleSynced(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testName]);

    function saveChanges() {
        if (testName) {
            if (currentTitle !== "" && isNameChanged) {
                setTestName(currentTitle);
            } else {
                setCurrentTitle(testName);
                setIsTitleSynced(true);
            }
        }
        setIsEditing(false);
    }

    function discardChanges() {
        setIsEditing(false);
        setIsNameChanged(false);
        setCurrentTitle(testName);
        setIsTitleSynced(true);
    }

    function updateTitle(e: ChangeEvent<HTMLInputElement>) {
        const value = e.target.value;
        setIsNameChanged(value !== testName);
        setCurrentTitle(value);
        if (testContent?.length > 0) setIsTitleSynced(false);
    }

    return (
        <Wrapper data-testid="exercise-sheet-title">
            <LabelWrapper>
                <Label>{t_exerciseTitle}</Label>
            </LabelWrapper>
            <InputWithActions
                data-testid="input-exercise-sheet-title"
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                currentText={currentTitle}
                onChange={updateTitle}
                onSave={saveChanges}
                onDiscard={discardChanges}
                isSaveButtonDisabled={currentTitle === ""}
            />
        </Wrapper>
    );
};

export default ExerciseSheetTitle;
